import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MessageCircle, X, Send, Sparkles } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate a unique session ID
const generateSessionId = () => {
  const stored = sessionStorage.getItem('she_session_id');
  if (stored) return stored;
  const newId = `she_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  sessionStorage.setItem('she_session_id', newId);
  return newId;
};

const SHEChatWidget = ({ pageContext = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "I AM SHE — your Sister Diamonds guide. I'm here to help you navigate, answer questions, or simply hold space. What's on your mind?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(generateSessionId);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/chat/she`, {
        message: userMessage,
        session_id: sessionId,
        page_context: pageContext
      });

      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm having a moment — my connection flickered. Would you try that again?" 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Chat Button - Lower Right Corner - High Visibility */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-[9999] transition-all duration-300 flex items-center gap-3 ${
          isOpen 
            ? 'bg-gray-700 hover:bg-gray-600 h-14 w-14 rounded-full justify-center shadow-lg' 
            : 'bg-forest hover:bg-forest/90 hover:shadow-2xl hover:scale-105 px-6 py-4 rounded-full shadow-[0_8px_30px_rgba(0,0,0,0.35)] border-[3px] border-terracotta'
        }`}
        data-testid="she-chat-toggle"
        aria-label={isOpen ? 'Close chat' : 'Open chat with SHE'}
      >
        {isOpen ? (
          <X className="h-6 w-6 text-white" />
        ) : (
          <>
            <div className="relative">
              <MessageCircle className="h-7 w-7 text-white" />
              <span className="absolute -top-1 -right-1 h-4 w-4 bg-terracotta rounded-full animate-pulse shadow-md border-2 border-white" />
            </div>
            <div className="flex flex-col items-start">
              <span className="text-white font-bold text-base tracking-wide drop-shadow-sm">I AM SHE</span>
              <span className="text-white/90 text-xs">Chat with me</span>
            </div>
          </>
        )}
      </button>

      {/* Chat Window - Positioned from Right */}
      {isOpen && (
        <div 
          className="fixed bottom-24 right-6 z-[9999] w-[360px] max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
          data-testid="she-chat-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-forest to-forest/90 px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-white">SHE</h3>
                <p className="text-xs text-white/80">Service • Humility • Excellence</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="h-[350px] overflow-y-auto p-4 space-y-4 bg-cream/30">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    message.role === 'user'
                      ? 'bg-forest text-white rounded-br-md'
                      : 'bg-white text-gray-800 rounded-bl-md shadow-sm border border-gray-100'
                  }`}
                >
                  {message.content}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 px-4 py-3 rounded-2xl rounded-bl-md shadow-sm border border-gray-100">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-terracotta rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-terracotta rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-terracotta rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-3 bg-white border-t border-gray-100">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask SHE anything..."
                className="flex-1 border-gray-200 focus:border-forest focus:ring-forest/20"
                disabled={isLoading}
                data-testid="she-chat-input"
              />
              <Button 
                type="submit" 
                size="icon"
                disabled={!input.trim() || isLoading}
                className="bg-terracotta hover:bg-terracotta/90 text-white h-10 w-10"
                data-testid="she-chat-send"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default SHEChatWidget;
